// Fundo do tabuleiro (loja VIP): arte gerada (assets/boards) + efeito animado.
// Usado no tabuleiro da partida e na prévia da loja.
import { Assets, Container, Graphics, Sprite, type Texture } from "pixi.js";

/** arte usada quando ninguém equipou tabuleiro */
export const DEFAULT_BOARD = "padrao";

interface Court { x: number; y: number; w: number; h: number }
interface BoardsIndex { [id: string]: { file: string; fx: string | null; court?: Court; edge?: string } }
let index: Promise<BoardsIndex> | null = null;
export function boardsIndex(): Promise<BoardsIndex> {
  index ??= fetch("/assets/boards-index.json").then((r) => (r.ok ? r.json() : {})).catch(() => ({}));
  return index;
}

interface Particle { g: Graphics; x: number; y: number; vx: number; vy: number; life: number; max: number; phase: number }

export class ArenaLayer extends Container {
  private fillBg = new Graphics();
  private bg = new Sprite();
  private court: Court | null = null;
  private edge = 0x000000;
  private field: Court | null = null;
  private fxLayer = new Container();
  private glow = new Graphics();
  private parts: Particle[] = [];
  private fx: string | null = null;
  private w = 0;
  private h = 0;
  private t = 0;
  private id: string | null = null;
  private token = 0;

  constructor() {
    super();
    this.addChild(this.fillBg, this.bg, this.glow, this.fxLayer);
    this.bg.visible = false;
  }

  get active(): boolean {
    return this.id !== null;
  }

  /** id do tabuleiro da loja; null = tabuleiro padrão (estádio) */
  async set(id: string | null): Promise<void> {
    id ??= DEFAULT_BOARD;
    if (id === this.id) return;
    this.id = id;
    const my = ++this.token;
    this.clearFx();
    const entry = (await boardsIndex())[id];
    if (!entry || my !== this.token) return;
    const tex = await Assets.load<Texture>(`/assets/${entry.file}`);
    if (my !== this.token || this.destroyed) return;
    tex.source.scaleMode = "nearest";
    this.bg.texture = tex;
    this.bg.visible = true;
    this.fx = entry.fx;
    this.court = entry.court ?? null;
    this.edge = entry.edge ? parseInt(entry.edge.slice(1), 16) : 0x000000;
    this.resize(this.w, this.h, this.field ?? undefined);
  }

  /**
   * @param field retângulo do tabuleiro de hexágonos na tela: a quadra da arte
   *   é alinhada a ele (meio-campo da arte = divisa entre os dois lados).
   */
  resize(w: number, h: number, field?: Court) {
    this.w = w;
    this.h = h;
    this.field = field ?? null;
    this.fillBg.clear();
    if (this.bg.visible && this.bg.texture.width > 1) {
      const tw = this.bg.texture.width, th = this.bg.texture.height;
      if (field && this.court) {
        const k = Math.max(field.w / (this.court.w * tw), field.h / (this.court.h * th));
        this.bg.scale.set(k);
        const cx = field.x + field.w / 2, cy = field.y + field.h / 2;
        this.bg.position.set(cx - (this.court.x + this.court.w / 2) * tw * k, cy - (this.court.y + this.court.h / 2) * th * k);
        this.fillBg.rect(0, 0, w, h).fill({ color: this.edge });
      } else {
        // "cover": preenche a área sem distorcer
        const k = Math.max(w / tw, h / th);
        this.bg.scale.set(k);
        this.bg.position.set((w - tw * k) / 2, (h - th * k) / 2);
      }
    }
    this.clearFx();
  }

  private clearFx() {
    for (const p of this.parts) p.g.destroy();
    this.parts = [];
    this.glow.clear();
  }

  private spawn(): Particle | null {
    const { w, h } = this;
    const r = Math.random;
    const g = new Graphics();
    let p: Omit<Particle, "g">;
    switch (this.fx) {
      case "snow": {
        const s = 1 + Math.floor(r() * 3);
        g.rect(0, 0, s, s).fill({ color: 0xffffff, alpha: 0.9 });
        p = { x: r() * w, y: -4, vx: (r() - 0.5) * 0.02, vy: 0.03 + r() * 0.04, life: 0, max: 20000, phase: r() * 6 };
        break;
      }
      case "embers": {
        const s = 2 + Math.floor(r() * 2);
        g.rect(0, 0, s, s).fill({ color: r() < 0.5 ? 0xffc02e : 0xff6a1f });
        p = { x: r() * w, y: h + 4, vx: (r() - 0.5) * 0.02, vy: -(0.03 + r() * 0.05), life: 0, max: 9000, phase: r() * 6 };
        break;
      }
      case "fireflies": {
        g.circle(0, 0, 4).fill({ color: 0xfff27a, alpha: 0.25 }).circle(0, 0, 1.6).fill({ color: 0xfffbc8 });
        p = { x: r() * w, y: r() * h, vx: (r() - 0.5) * 0.02, vy: (r() - 0.5) * 0.02, life: 0, max: 5000 + r() * 4000, phase: r() * 6 };
        break;
      }
      case "waves": {
        // espuma correndo na água (metade de cima)
        const len = 10 + r() * 24;
        g.rect(0, 0, len, 2).fill({ color: 0xffffff, alpha: 0.7 });
        p = { x: -len, y: Math.max(0, this.bg.y) + r() * this.bg.height * 0.07, vx: 0.02 + r() * 0.03, vy: 0, life: 0, max: 14000, phase: r() * 6 };
        break;
      }
      case "leaves": {
        g.rect(0, 0, 3, 2).fill({ color: r() < 0.5 ? 0x6abb55 : 0xd8a23a });
        p = { x: r() * w, y: -4, vx: 0.01 + r() * 0.02, vy: 0.025 + r() * 0.03, life: 0, max: 20000, phase: r() * 6 };
        break;
      }
      case "sparkles": {
        g.rect(-1, -3, 2, 6).fill({ color: 0xffffff }).rect(-3, -1, 6, 2).fill({ color: 0xffffff });
        p = { x: r() * w, y: r() * h, vx: 0, vy: 0, life: 0, max: 900 + r() * 600, phase: 0 };
        break;
      }
      default:
        g.destroy();
        return null;
    }
    this.fxLayer.addChild(g);
    return { g, ...p };
  }

  private target(): number {
    const area = (this.w * this.h) / 100000;
    switch (this.fx) {
      case "snow": return Math.round(60 * area);
      case "embers": return Math.round(28 * area);
      case "fireflies": return Math.round(16 * area);
      case "waves": return Math.round(14 * area);
      case "leaves": return Math.round(14 * area);
      case "sparkles": return Math.round(8 * area);
      default: return 0;
    }
  }

  update(dt: number) {
    if (!this.fx || !this.bg.visible) return;
    this.t += dt;
    while (this.parts.length < this.target()) {
      const p = this.spawn();
      if (!p) break;
      // espalha as primeiras partículas pela tela toda
      if (this.parts.length < this.target() && this.t < 50 && (this.fx === "snow" || this.fx === "leaves")) p.y = Math.random() * this.h;
      if (this.t < 50 && this.fx === "embers") p.y = Math.random() * this.h;
      if (this.t < 50 && this.fx === "waves") p.x = Math.random() * this.w;
      this.parts.push(p);
    }
    for (const p of this.parts) {
      p.life += dt;
      p.x += p.vx * dt + (this.fx === "snow" || this.fx === "leaves" ? Math.sin(this.t / 700 + p.phase) * 0.15 : 0);
      p.y += p.vy * dt;
      if (this.fx === "fireflies") {
        p.vx += (Math.random() - 0.5) * 0.002;
        p.vy += (Math.random() - 0.5) * 0.002;
        p.vx = Math.max(-0.03, Math.min(0.03, p.vx));
        p.vy = Math.max(-0.03, Math.min(0.03, p.vy));
        p.g.alpha = 0.4 + 0.6 * Math.abs(Math.sin(this.t / 500 + p.phase));
      } else if (this.fx === "embers") {
        p.g.alpha = Math.max(0, 1 - p.life / p.max);
      } else if (this.fx === "sparkles") {
        p.g.alpha = Math.sin((p.life / p.max) * Math.PI);
      } else if (this.fx === "waves") {
        p.g.alpha = 0.3 + 0.5 * Math.sin((p.life / p.max) * Math.PI);
      }
      p.g.position.set(Math.round(p.x), Math.round(p.y));
    }
    const dead = this.parts.filter((p) => p.life > p.max || p.y > this.h + 6 || p.y < -8 || p.x > this.w + 40 || p.x < -40);
    for (const p of dead) p.g.destroy();
    if (dead.length) this.parts = this.parts.filter((p) => !dead.includes(p));
    // brilho pulsante da lava
    if (this.fx === "embers") {
      this.glow.clear();
      const a = 0.08 + 0.06 * Math.sin(this.t / 600);
      this.glow.rect(0, 0, this.w, this.h).fill({ color: 0xff6a1f, alpha: a });
    }
  }
}
